import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="dashboard-collapsible"
export default class extends Controller {
  static targets = ["content", "icon", "toggle", "label"]
  static values = {
    expanded: { type: Boolean, default: false },
    expandLabel: { type: String, default: "查看詳細數據" },
    collapseLabel: { type: String, default: "收起數據" },
    swipeEnabled: { type: Boolean, default: true },
    persistState: { type: Boolean, default: true },
    storageKey: { type: String, default: "dashboard-collapsible-state" }
  }

  connect() {
    // Restore state from sessionStorage
    if (this.persistStateValue) {
      this.restoreState()
    }

    // Initialize UI state (restored from sessionStorage if available)
    this.updateUI()
    this.updatePeriodLinks()

    // Initialize swipe gesture tracking
    if (this.swipeEnabledValue) {
      this.touchStartY = 0
      this.touchStartTime = 0
      this.bindSwipeEvents()
    }
  }

  disconnect() {
    if (this.swipeEnabledValue) {
      this.unbindSwipeEvents()
    }
  }

  toggle() {
    this.expandedValue = !this.expandedValue
    this.updateUI()
    this.updatePeriodLinks()

    // Save state to sessionStorage
    if (this.persistStateValue) {
      this.saveState()
    }
  }

  // Handle keyboard events (Enter/Space) for accessibility
  handleKeydown(event) {
    // Only respond to Enter (13) or Space (32)
    if (event.key === 'Enter' || event.key === ' ' || event.keyCode === 13 || event.keyCode === 32) {
      event.preventDefault() // Prevent page scroll on Space
      this.toggle()
    }
  }

  // State persistence methods
  saveState() {
    try {
      sessionStorage.setItem(this.storageKeyValue, JSON.stringify({
        expanded: this.expandedValue,
        timestamp: Date.now()
      }))
    } catch (error) {
      console.warn('[DashboardCollapsible] Failed to save state:', error)
    }
  }

  restoreState() {
    try {
      const saved = sessionStorage.getItem(this.storageKeyValue)
      if (saved) {
        const state = JSON.parse(saved)
        this.expandedValue = state.expanded
      }
    } catch (error) {
      console.warn('[DashboardCollapsible] Failed to restore state:', error)
    }
  }

  // Update period selector links with current expanded state
  // This ensures the expanded state is preserved when clicking period tabs
  updatePeriodLinks() {
    const links = this.element.querySelectorAll('a[data-turbo-frame="dashboard-summary"]')
    links.forEach(link => {
      const url = new URL(link.href)
      url.searchParams.set('expanded', this.expandedValue.toString())
      link.href = url.toString()
    })
  }

  // Swipe gesture event binding
  bindSwipeEvents() {
    this.handleTouchStart = this.handleTouchStart.bind(this)
    this.handleTouchEnd = this.handleTouchEnd.bind(this)

    this.toggleTarget.addEventListener('touchstart', this.handleTouchStart, { passive: true })
    this.toggleTarget.addEventListener('touchend', this.handleTouchEnd, { passive: true })
  }

  unbindSwipeEvents() {
    this.toggleTarget.removeEventListener('touchstart', this.handleTouchStart)
    this.toggleTarget.removeEventListener('touchend', this.handleTouchEnd)
  }

  handleTouchStart(event) {
    this.touchStartY = event.touches[0].clientY
    this.touchStartTime = Date.now()
  }

  handleTouchEnd(event) {
    const touchEndY = event.changedTouches[0].clientY
    const deltaY = touchEndY - this.touchStartY
    const deltaTime = Date.now() - this.touchStartTime

    // Swipe threshold: 50px minimum, 500ms max duration (quick swipe)
    const SWIPE_THRESHOLD = 50
    const TIME_THRESHOLD = 500

    if (Math.abs(deltaY) > SWIPE_THRESHOLD && deltaTime < TIME_THRESHOLD) {
      if (deltaY < 0 && !this.expandedValue) {
        // Swipe up when collapsed → expand
        this.toggle()
      } else if (deltaY > 0 && this.expandedValue) {
        // Swipe down when expanded → collapse
        this.toggle()
      }
    }
  }

  updateUI() {
    const isExpanded = this.expandedValue

    // Update content visibility with smooth height transition
    if (isExpanded) {
      // Expand: use fixed large value instead of scrollHeight
      // scrollHeight returns 0 when element has max-height:0 (CSS constraint prevents layout)
      // Using fixed value works because CSS transition naturally stops at actual content height
      this.contentTarget.style.maxHeight = '2000px'
      this.contentTarget.classList.remove('opacity-0')

      // Initialize lazy-loaded charts
      this.initializeLazyCharts()
    } else {
      // Collapse: set max-height to 0
      this.contentTarget.style.maxHeight = '0'
      this.contentTarget.classList.add('opacity-0')
    }

    // Update ARIA attribute for accessibility
    this.toggleTarget.setAttribute('aria-expanded', isExpanded.toString())

    // Rotate icon (▼ → ▲)
    if (this.hasIconTarget) {
      if (isExpanded) {
        this.iconTarget.classList.add('rotate-180')
      } else {
        this.iconTarget.classList.remove('rotate-180')
      }
    }

    // Update label text
    if (this.hasLabelTarget) {
      this.labelTarget.textContent = isExpanded ? this.collapseLabelValue : this.expandLabelValue
    }
  }

  // Initialize lazy-loaded charts when expanded
  initializeLazyCharts() {
    // Wait for CSS transition to complete (300ms duration)
    // Chart.js needs valid parent dimensions to render correctly
    setTimeout(() => {
      // Find all chart controllers with lazy loading enabled
      const chartElements = this.contentTarget.querySelectorAll('[data-controller*="ads-trend-chart"][data-ads-trend-chart-lazy-value="true"]')

      chartElements.forEach(element => {
        // Get the Stimulus controller instance
        const controller = this.application.getControllerForElementAndIdentifier(element, 'ads-trend-chart')

        if (controller && typeof controller.reveal === 'function') {
          controller.reveal()
        }
      })
    }, 350)
  }

  // Handle window resize to update max-height when expanded
  expandedValueChanged() {
    if (this.expandedValue && this.hasContentTarget) {
      // Use fixed value (scrollHeight unreliable when max-height:0)
      this.contentTarget.style.maxHeight = '2000px'
    }
  }
}
